// BASELINE — lo normal de ESTE cliente, calculado en un solo lugar.
//
// Este archivo existe por una regla, y es la única que importa acá dentro:
//
//     Si un número aparece en el resumen Y en un bloque de detalle, tiene que
//     salir de la misma función. No de una copia, no de una fórmula
//     equivalente: de este archivo, importado por los dos.
//
// El motivo no es estético. La pantalla vieja tenía "tu mejor día de la semana"
// en dos lugares con dos cuentas distintas, y daban números distintos: uno
// promediaba metiendo los domingos cerrados como $0 y el otro no. El dueño no
// tiene forma de saber cuál de los dos está bien, así que deja de creerle a los
// dos — y de paso al resto de la pantalla. Un resumen que apunta al detalle es
// exactamente su función; lo que no se perdona es que el resumen y el detalle
// digan cosas distintas.
//
// Cada vez que un cálculo se mude acá, el bloque que lo tenía adentro TIENE que
// pasar a importarlo. Dejar la copia vieja "por las dudas" es peor que no haber
// movido nada: quedan dos implementaciones y ya nadie sabe cuál se está viendo.
//
// Las tres reglas de método que sostienen todo lo de abajo:
//
// 1. MEDIANA, NUNCA PROMEDIO. Un sábado con un evento al lado corre el promedio
//    de los sábados para todo el trimestre y deja la vara mal puesta. La
//    mediana —el valor del medio— no se mueve.
//
// 2. UN DÍA CERRADO NO VALE $0: NO VALE NADA. Son cosas distintas. Contar los
//    días sin operar como cero hunde todos los promedios de la pantalla, y
//    justo por haber estado cerrado.
//
// 3. LOS DÍAS DE LA SEMANA EN QUE EL CLIENTE NO ABRE SE DEDUCEN, NO SE
//    PREGUNTAN. Nadie va a cargar su horario en una pantalla de estadísticas.

// ── Constantes de método ─────────────────────────────────────────────────
//
// Los números de acá no son arbitrarios y conviene saber qué se rompe si se
// tocan.

/** De dónde sale el patrón: 90 días son ~13 ocurrencias por día de la semana. */
export const VENTANA_PATRON = 90;

/** Menos de un mes son 4 muestras por día de la semana: eso no es un patrón. */
export const MIN_HISTORIA = 30;

/** Por día de la semana, para poder afirmar cuánto te deja ese día. */
export const MIN_MUESTRAS = 3;

/** Por día de la semana, para tener banda p25–p75. */
export const MIN_BANDA = 4;

/**
 * Días CON MOVIMIENTO que hacen falta para afirmar cuál es "un día tuyo".
 *
 * Vive acá y no en `TuTecho` porque son dos bloques los que hablan de ese día:
 * `TuTecho` lo publica ("Un día tuyo: 32 autos") y el resumen lo deriva ("cada
 * cochera recibe 0,46 autos en un día normal"). Con el mínimo escrito en un
 * solo lado, el resumen afirmaba cuál era el día normal de un cliente de doce
 * días mientras el bloque de abajo le decía, con todas las letras, que todavía
 * no lo sabía.
 */
export const MIN_DIA_TIPICO = 30;

export const DIAS_LARGO = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];
export const DIAS_CORTO = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

// La semana arranca el lunes. No es un detalle: con el domingo primero, el fin
// de semana queda partido en las dos puntas del gráfico y deja de leerse como
// un bloque.
export const ORDEN_SEMANA = [1, 2, 3, 4, 5, 6, 0];

// ── Utilidades ───────────────────────────────────────────────────────────

/** Fecha local en ISO. `toISOString()` es UTC y después de las 21:00 en
 *  Argentina devuelve el día siguiente: "hoy" se adelantaría un día. */
export const isoLocal = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export const dowDe = (iso) => new Date(`${iso}T00:00:00`).getDay();

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** ¿Ese día la playa operó? Un día cerrado no vale $0: no vale nada. */
export const opero = (d) => num(d?.income) > 0 || num(d?.tickets?.total?.in) > 0;

/** El negado, para los bloques que preguntan al revés. Es el mismo predicado:
 *  tener dos que casi coinciden es la forma más rápida de que dos bloques
 *  descarten días distintos y terminen mostrando números distintos. */
export const noOpero = (d) => !opero(d);

/** Vehículos que salieron ese día. La plata entra cuando el auto SALE, así que
 *  las cuentas de rotación se hacen sobre las salidas. */
export const salidasDe = (d) => num(d?.tickets?.total?.out);

/** Mediana. Resistente al día extraordinario, que es exactamente el problema. */
export const mediana = (nums) => {
  const o = (nums || []).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
  if (!o.length) return null;
  const m = Math.floor(o.length / 2);
  return o.length % 2 ? o[m] : (o[m - 1] + o[m]) / 2;
};

/** Cuantil con interpolación lineal, sobre un array YA ordenado. */
export const cuantil = (ordenados, q) => {
  if (!ordenados.length) return null;
  const pos = (ordenados.length - 1) * q;
  const bajo = Math.floor(pos);
  const resto = pos - bajo;
  const siguiente = ordenados[bajo + 1];
  return siguiente === undefined ? ordenados[bajo] : ordenados[bajo] + resto * (siguiente - ordenados[bajo]);
};

/** "los sábados" / "los martes". Sólo sábado y domingo cambian en plural: el
 *  resto ya termina en "s" y pluralizarlos otra vez da "martess". */
export const enPlural = (dow) => {
  const n = DIAS_LARGO[dow].toLowerCase();
  return `los ${dow === 6 || dow === 0 ? `${n}s` : n}`;
};

/** "jueves" — el nombre suelto, para meterlo en el medio de una frase. */
export const nombreDia = (dow) => DIAS_LARGO[dow].toLowerCase();

// ── Qué días de la semana el cliente no abre ─────────────────────────────
//
// Se deduce del comportamiento, no se le pregunta. Un día de la semana se
// considera "habitualmente cerrado" si aparece al menos 4 veces y en el 80% o
// más de esas veces no hubo ni un peso ni un auto.
//
// El 80% y no el 100% es deliberado: el que abre un domingo al año para un
// evento no deja de ser "un local que no abre los domingos", y si se exigiera
// el 100% ese único domingo devolvería los otros doce ceros al promedio.
export function diasCerradosHabituales(docs) {
  const conteo = {};
  (docs || []).forEach((d) => {
    if (!d?.date) return;
    const k = dowDe(d.date);
    conteo[k] = conteo[k] || { total: 0, sin: 0 };
    conteo[k].total += 1;
    if (noOpero(d)) conteo[k].sin += 1;
  });
  const cerrados = new Set();
  Object.keys(conteo).forEach((k) => {
    const c = conteo[k];
    if (c.total >= 4 && c.sin / c.total >= 0.8) cerrados.add(Number(k));
  });
  return cerrados;
}

/**
 * Mediana y banda p25–p75 del ingreso, por día de la semana.
 *
 * Excluye los días sin operar. NO excluye los días en los que entraron autos y
 * no se cobró nada: eso es un día real con un problema real, y sacarlo del
 * cálculo sería esconder justo lo que hay que ver.
 */
export function baselinePorDow(docs, dowCerrados) {
  const porDow = {};
  (docs || []).forEach((d) => {
    if (!d?.date) return;
    const k = dowDe(d.date);
    if (dowCerrados.has(k)) return;
    if (noOpero(d)) return;
    (porDow[k] = porDow[k] || []).push(num(d.income));
  });

  const salida = {};
  Object.keys(porDow).forEach((k) => {
    const vals = porDow[k].slice().sort((a, b) => a - b);
    salida[k] = {
      n: vals.length,
      mediana: mediana(vals),
      p25: vals.length >= MIN_BANDA ? cuantil(vals, 0.25) : null,
      p75: vals.length >= MIN_BANDA ? cuantil(vals, 0.75) : null,
    };
  });
  return salida;
}

/**
 * EL PATRÓN DE LA SEMANA — la respuesta a "qué día me deja más plata".
 *
 * Es la fuente única de `TusDias` y de los renglones de "Lo que salta a la
 * vista" que hablan de días de la semana: los dos muestran el mismo peso del
 * mismo jueves porque los dos llaman acá.
 *
 * `peor` es el día más flojo ENTRE LOS QUE EL CLIENTE ABRE, y ése es el bug
 * histórico que esta función cierra: los días habitualmente cerrados nunca
 * entran a `base`, así que no pueden salir elegidos. La pantalla vieja le decía
 * "el domingo te deja $0" a alguien que los domingos no abre.
 *
 * `suficiente` es lo único que decide si se puede AFIRMAR algo. Se devuelve
 * siempre —en vez de cortar con un `null`— para que quien necesite la vara de
 * un día puntual (la racha, por ejemplo) la tenga aunque todavía no haya patrón
 * que dibujar. Lo que no puede pasar es que un bloque afirme "tu mejor día es
 * el jueves" mientras otro dice "todavía no se ve el patrón de tu semana", y
 * por eso los dos consultan este mismo campo.
 */
export function patronSemanal(cerrados = []) {
  const docs = Array.isArray(cerrados) ? cerrados : [];

  // `cerrados` viene ascendente: los últimos 90 son la cola del array.
  const ventana = docs.slice(-VENTANA_PATRON);
  const dowCerrados = diasCerradosHabituales(ventana);
  const base = baselinePorDow(ventana, dowCerrados);

  const medidos = ORDEN_SEMANA
    .map((dow) => ({ dow, ...(base[dow] || { n: 0, mediana: null, p25: null, p75: null }) }))
    .filter((d) => d.n >= MIN_MUESTRAS && d.mediana !== null);

  const ordenados = medidos.slice().sort((a, b) => b.mediana - a.mediana);

  // Días con movimiento REAL dentro de la ventana: es lo que se declara en el
  // rango. Contar los cerrados infla la muestra y hace que "90 días" sean 64.
  const operados = ventana.filter((d) => d?.date && opero(d) && !dowCerrados.has(dowDe(d.date)));

  return {
    base,
    dowCerrados,
    medidos,
    mejor: ordenados.length ? ordenados[0] : null,
    peor: ordenados.length ? ordenados[ordenados.length - 1] : null,
    // Con menos de tres días de la semana medidos no hay "patrón de la semana":
    // hay tres barras sueltas. Eso no se dibuja, y tampoco se afirma.
    suficiente: docs.length >= MIN_HISTORIA && medidos.length >= 3,
    dias: docs.length,
    diasOperados: operados.length,
    desde: operados.length ? operados[0].date : ventana[0]?.date || null,
    hasta: operados.length ? operados[operados.length - 1].date : ventana[ventana.length - 1]?.date || null,
    pocasMuestras: ORDEN_SEMANA.filter(
      (dow) => !dowCerrados.has(dow) && (!base[dow] || base[dow].n < MIN_MUESTRAS)
    ),
  };
}

/**
 * LA VARA DE UN DÍA SUELTO — cuánto deja habitualmente el día de la semana al
 * que pertenece esa fecha, o `null` si todavía no se puede afirmar.
 *
 * Existe para que "este día viene por encima de lo habitual" se mida contra
 * exactamente el mismo número que la pantalla dibuja en el gráfico de la
 * semana. Cuando cada bloque se armaba su propia referencia, la racha se
 * cortaba o se estiraba según qué días habían caído en su ventana, y no había
 * forma de explicarle al dueño por qué.
 */
export function referenciaDelDia(patron, iso) {
  if (!patron || !iso) return null;
  const b = patron.base[dowDe(iso)];
  return b && b.n >= MIN_MUESTRAS && b.mediana > 0 ? b.mediana : null;
}

/**
 * UN DÍA TUYO — cuántos vehículos mueve la playa en una jornada normal.
 *
 * Mediana de las SALIDAS sobre los días que realmente operaron. Es la fuente
 * única de "Tu techo" y del renglón de la cochera en el resumen: si alguien
 * divide los autos por las cocheras a mano, le tiene que dar lo mismo que
 * muestra la pantalla.
 *
 * Dos decisiones que, si se "simplifican", rompen los dos bloques a la vez:
 *
 * · SALIDAS, NO ENTRADAS. Un auto que entró y todavía no salió no completó una
 *   rotación de la cochera, que es lo que se está midiendo.
 *
 * · LOS DÍAS SIN SALIDAS QUEDAN AFUERA. Un domingo cerrado no describe cómo es
 *   un domingo: describe que no se abrió. Contarlo como 0 autos hunde la
 *   mediana.
 *
 * Devuelve también los documentos que entraron (`operados`) porque quien
 * necesite la curva de ocupación de "un día tuyo" tiene que promediar
 * exactamente esos días, y no la ventana entera.
 */
export function diaTipico(cerrados = [], ventana = VENTANA_PATRON) {
  const docs = (Array.isArray(cerrados) ? cerrados : []).slice(-ventana);
  const operados = docs.filter((d) => salidasDe(d) > 0);
  const autos = mediana(operados.map((d) => salidasDe(d)));

  return {
    operados,
    dias: operados.length,
    desde: operados.length ? operados[0].date : null,
    hasta: operados.length ? operados[operados.length - 1].date : null,
    autos,
    // EL NÚMERO QUE SE MUESTRA, ya redondeado. No es una comodidad: con una
    // cantidad par de días la mediana cae en un medio (8,5 autos), `TuTecho`
    // la publica como 9 y el resumen dividía el 8,5 crudo — así "0,12 autos
    // por cochera × 70 cocheras" daba 8 donde el bloque de abajo decía 9.
    // Todo lo que se derive de "un día tuyo" tiene que derivarse de ESTE
    // número, que es el que el dueño tiene delante.
    autosMostrados: autos === null ? null : Math.round(autos),
  };
}
